import { defineStore } from 'pinia'
import { type Component, ref } from 'vue'

export interface ModalOptions {
  width?: string | number
  maxWidth?: string | number
  persistent?: boolean
  closable?: boolean
  scrollable?: boolean
  fullscreen?: boolean
  transition?: string
  title?: string
}

export interface ModalInstance {
  id: string
  component: Component
  props: Record<string, any>
  options: ModalOptions
  resolve?: (value?: any) => void
  reject?: (reason?: any) => void
  isOpen: boolean
  isClosing: boolean
}

export const useModalStore = defineStore('modal', () => {
  const modals = ref<ModalInstance[]>([])
  let modalCounter = 0

  const addModal = (modal: ModalInstance) => {
    modals.value.push(modal)
  }

  const removeModal = (id: string) => {
    const index = modals.value.findIndex((modal) => modal.id === id)
    if (index !== -1) {
      modals.value.splice(index, 1)
    }
  }

  const openModal = <T = any>(
    component: Component,
    options: ModalOptions = {},
    props: Record<string, any> = {},
  ): Promise<T> => {
    return new Promise((resolve, reject) => {
      const id = `modal-${++modalCounter}`
      const modal: ModalInstance = {
        id,
        component,
        props,
        options: {
          width: 'auto',
          persistent: false,
          closable: true,
          scrollable: true,
          ...options,
        },
        resolve,
        reject,
        isOpen: true,
        isClosing: false,
      }
      addModal(modal)
    })
  }

  const closeModal = (id: string, result?: any) => {
    const modal = modals.value.find((m) => m.id === id)
    if (modal && !modal.isClosing) {
      modal.isOpen = false
      modal.isClosing = true

      // Delay the actual removal to allow transition to complete
      setTimeout(() => {
        if (modal.resolve) {
          modal.resolve(result)
        }
        removeModal(id)
      }, 300) // Default Vuetify transition duration
    }
  }

  const dismissModal = (id: string, reason?: any) => {
    const modal = modals.value.find((m) => m.id === id)
    if (modal && !modal.isClosing) {
      modal.isOpen = false
      modal.isClosing = true

      // Delay the actual removal to allow transition to complete
      setTimeout(() => {
        if (modal.reject) {
          modal.reject(reason)
        }
        removeModal(id)
      }, 300) // Default Vuetify transition duration
    }
  }

  const closeAllModals = () => {
    modals.value.forEach((modal) => {
      if (modal.reject) {
        modal.reject('All modals closed')
      }
    })
    modals.value = []
  }

  return {
    modals,
    addModal,
    removeModal,
    openModal,
    closeModal,
    dismissModal,
    closeAllModals,
  }
})
